import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import Container from "@/components/base/Container";
import Footer from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import useSeo from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { formatHeat, getCategoryName } from "@/utils/content";
import RelatedList from "./components/RelatedList";

const BASE = "https://iosfxw.cn";

export default function TopicDetail() {
  const { slug } = useParams();
  const article = articles.find((item) => item.slug === slug);
  const [copied, setCopied] = useState(false);

  const toc = useMemo(
    () =>
      (article?.blocks ?? [])
        .map((block, index) => ({ block, index }))
        .filter((entry) => entry.block.type === "h2"),
    [article],
  );

  const related = useMemo(() => {
    if (!article) return [];
    const same = articles.filter((item) => item.category === article.category && item.id !== article.id);
    const others = articles.filter((item) => item.category !== article.category && item.id !== article.id);
    return [...same, ...others].slice(0, 3);
  }, [article]);

  useSeo({
    title: article
      ? `${article.title}｜五一假期事件盘点 - 瓜田速报`
      : "文章未找到 - 瓜田速报",
    description: article
      ? `${article.excerpt}（分类：${getCategoryName(article.category)}，更新于 ${article.dateText}）`
      : "该篇盘点内容可能已调整或移除，请返回盘点列表查看最新文章。",
    keywords: article ? article.tags.join(",") : "五一假期事件盘点",
    canonical: article ? `${BASE}/topics/${article.slug}` : `${BASE}/topics`,
    type: "article",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            image: article.cover,
            datePublished: article.date,
            dateModified: article.date,
            articleSection: getCategoryName(article.category),
            inLanguage: "zh-CN",
            author: { "@type": "Organization", name: "瓜田速报" },
            publisher: { "@type": "Organization", name: "瓜田速报" },
            mainEntityOfPage: `${BASE}/topics/${article.slug}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${BASE}/` },
              { "@type": "ListItem", position: 2, name: "事件盘点", item: `${BASE}/topics` },
              { "@type": "ListItem", position: 3, name: article.title, item: `${BASE}/topics/${article.slug}` },
            ],
          },
        ]
      : undefined,
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="flex min-h-[70vh] items-center justify-center px-4 pt-16 md:pt-[74px]">
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-8 py-14 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-article-line text-2xl text-foreground-400" />
            </span>
            <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">没有找到这篇盘点</h1>
            <p className="mt-2 max-w-sm text-sm text-foreground-500">
              它可能已经被合并到其他文章中，返回列表看看最新发布的盘点内容。
            </p>
            <Link
              to="/topics"
              className="mt-6 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-left-line" />
              返回盘点列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-[74px]">
        <article>
          <header className="border-b border-background-200 bg-background-100 py-10 md:py-14">
            <Container>
              <Breadcrumbs
                items={[
                  { label: "首页", to: "/" },
                  { label: "事件盘点", to: "/topics" },
                  { label: getCategoryName(article.category), to: `/topics?cat=${article.category}` },
                  { label: article.title },
                ]}
              />
              <div className="mt-5 max-w-3xl">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/topics?cat=${article.category}`}
                    className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
                  >
                    {getCategoryName(article.category)}
                  </Link>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
                <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-[40px] md:leading-tight">
                  {article.title}
                </h1>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                  {article.excerpt}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 md:text-sm">
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-user-line" />
                    {article.author}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-calendar-line" />
                    {article.dateText} 发布
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-time-line" />
                    阅读约 {article.readTime}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-fire-line" />
                    热度 {formatHeat(article.hot)}
                  </span>
                </div>
              </div>
            </Container>
          </header>

          <div className="bg-background-50 py-10 md:py-14">
            <Container>
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
                <div className="lg:col-span-8">
                  <figure className="overflow-hidden rounded-lg border border-background-200">
                    <div className="h-56 w-full bg-background-200 md:h-96">
                      <img
                        src={article.cover}
                        alt={article.title}
                        title={`${article.title} - 五一假期事件盘点`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <figcaption className="border-t border-background-200 bg-background-100 px-4 py-2.5 text-xs text-foreground-500">
                      配图为专题插画，与文中提及的任何具体主体无对应关系。
                    </figcaption>
                  </figure>

                  <div className="content-prose mt-8 text-[15px] text-foreground-800">
                    {article.blocks.map((block, index) => {
                      if (block.type === "h2") {
                        return (
                          <h2
                            id={`sec-${index}`}
                            key={index}
                            className="mb-4 mt-9 scroll-mt-28 font-heading text-xl font-bold text-foreground-950 md:text-2xl"
                          >
                            {block.text}
                          </h2>
                        );
                      }
                      if (block.type === "h3") {
                        return (
                          <h3
                            key={index}
                            className="mb-3 mt-7 font-heading text-lg font-semibold text-foreground-900"
                          >
                            {block.text}
                          </h3>
                        );
                      }
                      if (block.type === "quote") {
                        return (
                          <blockquote
                            key={index}
                            className="my-6 rounded-r-md border-l-4 border-accent-500 bg-accent-50 px-5 py-4 font-heading text-[15px] leading-relaxed text-foreground-800"
                          >
                            {block.text}
                          </blockquote>
                        );
                      }
                      if (block.type === "list") {
                        return (
                          <ul key={index} className="my-5 space-y-3">
                            {block.items?.map((item) => (
                              <li key={item} className="flex items-start gap-3 text-[15px] leading-relaxed">
                                <i className="ri-checkbox-blank-circle-fill mt-2 text-[6px] text-primary-500" />
                                <span className="text-foreground-700">{item}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      }
                      return (
                        <p key={index} className="text-foreground-700">
                          {block.text}
                        </p>
                      );
                    })}
                  </div>

                  <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
                    <p className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-600">
                      <i className="ri-shield-check-line mt-0.5 text-lg text-secondary-600" />
                      本文为公开信息整理与时间线复盘，不针对任何具体个人作出事实认定。若发现内容存在偏差，欢迎通过联系页面提交更正说明。
                    </p>
                  </div>

                  <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-6">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-foreground-500">分享这篇盘点：</span>
                      <button
                        type="button"
                        onClick={handleCopy}
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                      >
                        <i className={copied ? "ri-check-line" : "ri-link"} />
                        {copied ? "链接已复制" : "复制链接"}
                      </button>
                      <a
                        rel="nofollow noopener"
                        href="https://service.weibo.com/share/share.php?url=https%3A%2F%2Fwww.example-guafield-report.com%2Ftopics"
                        target="_blank"
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                      >
                        <i className="ri-share-forward-line" />
                        分享出去
                      </a>
                    </div>
                    <Link
                      to={`/topics?cat=${article.category}`}
                      className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                    >
                      查看更多{getCategoryName(article.category)}盘点
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </div>

                <aside className="lg:col-span-4">
                  <div className="lg:sticky lg:top-24">
                    {toc.length > 0 && (
                      <nav
                        aria-label="文章目录"
                        className="rounded-lg border border-background-200 bg-background-50 p-5"
                      >
                        <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                          <i className="ri-list-check-2 text-primary-600" />
                          文章目录
                        </h2>
                        <ul className="mt-4 space-y-2.5">
                          {toc.map((entry, order) => (
                            <li key={entry.index}>
                              <a
                                href={`#sec-${entry.index}`}
                                className="flex items-start gap-2.5 text-sm text-foreground-600 transition-colors hover:text-primary-700"
                              >
                                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm bg-background-100 text-[11px] font-medium text-foreground-500">
                                  {order + 1}
                                </span>
                                {entry.block.text}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </nav>
                    )}

                    <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
                      <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                        <i className="ri-information-line text-accent-600" />
                        阅读提示
                      </h2>
                      <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                        文中时间与分类基于公开讨论整理，热度值仅表示相对活跃度。建议结合原始来源交叉核对，再形成自己的判断。
                      </p>
                      <Link
                        to="/about#principle"
                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                      >
                        查看内容原则
                        <i className="ri-arrow-right-line" />
                      </Link>
                    </div>
                  </div>
                </aside>
              </div>
            </Container>
          </div>
        </article>

        <RelatedList article={article} related={related} />
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}